import React, { useState, useEffect, useCallback } from 'react';
import { MdPerson, MdSettings } from 'react-icons/md';

/**
 * UserSettings Component
 * Page for managing user profile and code editor settings
 * Renders in App.jsx main content area (same level as About, Help, etc.)
 */
const UserSettings = ({ username, userRole, apiBaseUrl }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [errorMessage, setErrorMessage] = useState('');

  // Profile settings
  const [profileSettings, setProfileSettings] = useState({
    firstname: '',
    lastname: '',
    email: username || '',
    role: userRole || 'Student'
  });

  // Code Editor Settings
  const [editorSettings, setEditorSettings] = useState(null);
  const [isSavingEditor, setIsSavingEditor] = useState(false);
  const [editorSaveMessage, setEditorSaveMessage] = useState('');
  const [hasEditorChanges, setHasEditorChanges] = useState(false);

  const loadUserProfile = useCallback(async () => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/user-profile/${encodeURIComponent(username)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'User-Role': userRole,
            'Requesting-User': username
          },
          credentials: 'include' // Important: include cookies for session auth
        }
      );

      const data = await response.json();

      console.log('User profile response:', data);

      if (data.success && data.user) {
        console.log('Setting profile settings with:', {
          firstname: data.user.firstname,
          lastname: data.user.lastname,
          email: data.user.username,
          role: data.user.role
        });

        setProfileSettings({
          firstname: data.user.firstname || '',
          lastname: data.user.lastname || '',
          email: data.user.username || username,
          role: data.user.role || userRole
        });
      } else {
        console.error('User profile fetch failed or no user data:', data);
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      setErrorMessage('Failed to load profile data');
    }
  }, [username, userRole, apiBaseUrl]);

  // Load editor settings
  const loadEditorSettings = useCallback(async () => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/user/${encodeURIComponent(username)}/editor-settings`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'User-Role': userRole,
            'Requesting-User': username
          },
          credentials: 'include'
        }
      );

      const data = await response.json();

      if (data.success && data.settings) {
        setEditorSettings(data.settings);
      } else {
        console.error('Failed to load editor settings:', data.error);
      }
    } catch (error) {
      console.error('Error loading editor settings:', error);
    }
  }, [username, userRole, apiBaseUrl]);

  // Load user profile and editor settings when component mounts
  useEffect(() => {
    loadUserProfile();
    loadEditorSettings();
  }, [loadUserProfile, loadEditorSettings]);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: MdPerson },
    { id: 'editor', label: 'Code Editor', icon: MdSettings }
  ];

  // Default editor settings structure
  const getDefaultEditorSettings = () => ({
    outputPlacement: 'bottom',
    theme: 'vs-dark',
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    fontSize: 14,
    fontLigatures: true,
    lineHeight: 1.5,
    letterSpacing: 0,
    minimapEnabled: false,
    minimapRenderCharacters: true,
    minimapMaxColumn: 120,
    renderWhitespace: 'none',
    renderControlCharacters: false,
    cursorStyle: 'line',
    cursorBlinking: 'blink',
    wordWrap: 'on',
    wordWrapColumn: 80,
    lineNumbers: 'on',
    tabSize: 4,
    insertSpaces: true,
    autoClosingBrackets: 'always',
    autoClosingQuotes: 'always',
    autoClosingDelete: 'auto',
    autoIndent: 'full',
    folding: true,
    foldingHighlight: true,
    codeLens: true,
    quickSuggestions: true,
    parameterHintsEnabled: true,
    acceptSuggestionOnEnter: 'on',
    rulers: [],
    scrollBeyondLastLine: false,
    smoothScrolling: true,
    stickyScrollEnabled: false
  });

  const currentEditorSettings = { ...getDefaultEditorSettings(), ...editorSettings };

  const handleEditorSettingChange = (key, value) => {
    const newSettings = {
      ...editorSettings,
      [key]: value
    };
    setEditorSettings(newSettings);
    setHasEditorChanges(true);
    setEditorSaveMessage('');
  };

  const handleSaveEditorSettings = async () => {
    setIsSavingEditor(true);
    setEditorSaveMessage('');

    try {
      const response = await fetch(
        `${apiBaseUrl}/user/${encodeURIComponent(username)}/editor-settings`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'User-Role': userRole,
            'Requesting-User': username
          },
          credentials: 'include',
          body: JSON.stringify({ settings: editorSettings })
        }
      );

      const data = await response.json();

      if (data.success) {
        setHasEditorChanges(false);
        setEditorSaveMessage('Settings saved successfully!');
        setTimeout(() => setEditorSaveMessage(''), 3000);
      } else {
        setEditorSaveMessage('Failed to save settings. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save editor settings:', error);
      setEditorSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSavingEditor(false);
    }
  };

  const handleResetEditorSettings = async () => {
    setIsSavingEditor(true);
    setEditorSaveMessage('');

    try {
      const response = await fetch(
        `${apiBaseUrl}/user/${encodeURIComponent(username)}/editor-settings/reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'User-Role': userRole,
            'Requesting-User': username
          },
          credentials: 'include'
        }
      );

      const data = await response.json();

      if (data.success && data.settings) {
        setEditorSettings(data.settings);
        setHasEditorChanges(false);
        setEditorSaveMessage('Settings reset to defaults successfully!');
        setTimeout(() => setEditorSaveMessage(''), 3000);
      } else {
        setEditorSaveMessage('Failed to reset settings. Please try again.');
      }
    } catch (error) {
      console.error('Failed to reset editor settings:', error);
      setEditorSaveMessage('Failed to reset settings. Please try again.');
    } finally {
      setIsSavingEditor(false);
    }
  };

  // Render functions for Code Editor Settings tabs
  const themes = [
    { value: 'vs', label: 'Light (VS)' },
    { value: 'vs-dark', label: 'Dark (VS Dark)' },
    { value: 'hc-black', label: 'High Contrast Black' }
  ];

  const fontFamilies = [
    'Monaco, Menlo, "Ubuntu Mono", monospace',
    '"Fira Code", monospace',
    '"JetBrains Mono", monospace',
    '"Cascadia Code", monospace',
    '"Source Code Pro", monospace',
    'Consolas, monospace'
  ];

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      {/* Theme Selection */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Theme</h4>
        <select
          value={currentEditorSettings.theme}
          onChange={(e) => handleEditorSettingChange('theme', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {themes.map(theme => (
            <option key={theme.value} value={theme.value}>{theme.label}</option>
          ))}
        </select>
      </div>

      {/* Font Settings */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Font</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Font Family</label>
            <select
              value={currentEditorSettings.fontFamily}
              onChange={(e) => handleEditorSettingChange('fontFamily', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {fontFamilies.map(font => (
                <option key={font} value={font}>{font.split(',')[0].replace(/"/g, '')}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Font Size</label>
            <input
              type="number"
              min="10"
              max="24"
              value={currentEditorSettings.fontSize}
              onChange={(e) => handleEditorSettingChange('fontSize', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Line Height</label>
            <input
              type="number"
              min="1"
              max="3"
              step="0.1"
              value={currentEditorSettings.lineHeight}
              onChange={(e) => handleEditorSettingChange('lineHeight', parseFloat(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Letter Spacing</label>
            <input
              type="number"
              min="-2"
              max="5"
              step="0.1"
              value={currentEditorSettings.letterSpacing}
              onChange={(e) => handleEditorSettingChange('letterSpacing', parseFloat(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Minimap */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Minimap</h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={currentEditorSettings.minimapEnabled}
              onChange={(e) => handleEditorSettingChange('minimapEnabled', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Show minimap</span>
          </label>
        </div>
      </div>

      {/* Cursor Style */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Cursor & Visual</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Cursor Style</label>
            <select
              value={currentEditorSettings.cursorStyle}
              onChange={(e) => handleEditorSettingChange('cursorStyle', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="line">Line</option>
              <option value="block">Block</option>
              <option value="underline">Underline</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Cursor Blinking</label>
            <select
              value={currentEditorSettings.cursorBlinking}
              onChange={(e) => handleEditorSettingChange('cursorBlinking', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="blink">Blink</option>
              <option value="smooth">Smooth</option>
              <option value="phase">Phase</option>
              <option value="expand">Expand</option>
              <option value="solid">Solid</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBehaviorSettings = () => (
    <div className="space-y-6">
      {/* Tab Size */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Indentation</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Tab Size</label>
            <input
              type="number"
              min="1"
              max="8"
              value={currentEditorSettings.tabSize}
              onChange={(e) => handleEditorSettingChange('tabSize', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={currentEditorSettings.insertSpaces}
                onChange={(e) => handleEditorSettingChange('insertSpaces', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Insert spaces</span>
            </label>
          </div>
        </div>
      </div>

      {/* Word Wrap */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Word Wrap</h4>
        <select
          value={currentEditorSettings.wordWrap}
          onChange={(e) => handleEditorSettingChange('wordWrap', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="off">Off</option>
          <option value="on">On</option>
          <option value="wordWrapColumn">At Column</option>
          <option value="bounded">Bounded</option>
        </select>
      </div>

      {/* Line Numbers */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Line Numbers</h4>
        <select
          value={currentEditorSettings.lineNumbers}
          onChange={(e) => handleEditorSettingChange('lineNumbers', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="on">On</option>
          <option value="off">Off</option>
          <option value="relative">Relative</option>
        </select>
      </div>

      {/* IntelliSense */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">IntelliSense</h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={currentEditorSettings.quickSuggestions}
              onChange={(e) => handleEditorSettingChange('quickSuggestions', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Quick suggestions</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={currentEditorSettings.parameterHintsEnabled}
              onChange={(e) => handleEditorSettingChange('parameterHintsEnabled', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Parameter hints</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderLayoutSettings = () => (
    <div className="space-y-6">
      {/* Output Pane Placement */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Output Pane Placement</h4>
        <div className="space-y-2">
          <label className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
            <input
              type="radio"
              name="outputPlacement"
              value="bottom"
              checked={currentEditorSettings.outputPlacement === 'bottom'}
              onChange={(e) => handleEditorSettingChange('outputPlacement', e.target.value)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">Below Editor</div>
              <div className="text-sm text-gray-500">Output appears below the code editor</div>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
            <input
              type="radio"
              name="outputPlacement"
              value="right"
              checked={currentEditorSettings.outputPlacement === 'right'}
              onChange={(e) => handleEditorSettingChange('outputPlacement', e.target.value)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">Right Side</div>
              <div className="text-sm text-gray-500">Output appears to the right of the code editor</div>
            </div>
          </label>
        </div>
      </div>

      {/* Scrolling Options */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Scrolling</h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={currentEditorSettings.smoothScrolling}
              onChange={(e) => handleEditorSettingChange('smoothScrolling', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Smooth scrolling</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={currentEditorSettings.scrollBeyondLastLine}
              onChange={(e) => handleEditorSettingChange('scrollBeyondLastLine', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Scroll beyond last line</span>
          </label>
        </div>
      </div>
    </div>
  );

  const [editorSubTab, setEditorSubTab] = useState('appearance');
  const editorTabs = [
    { id: 'appearance', label: 'Appearance' },
    { id: 'behavior', label: 'Behavior' },
    { id: 'layout', label: 'Layout' }
  ];

  return (
    <div className="bg-gray-50 rounded-lg">
      {/* Header - matching About.jsx style */}
      <div className="bg-white shadow-sm border-b rounded-t-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="min-h-[72px] flex items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MdSettings className="text-blue-600 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">User Settings</h1>
                <p className="text-gray-600 text-sm">Manage your profile and preferences</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - matching ClassHome.jsx container style */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <div className="flex px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-colors font-medium ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={20} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Profile Information</h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={profileSettings.firstname}
                          readOnly
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={profileSettings.lastname}
                          readOnly
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email / Username
                      </label>
                      <input
                        type="email"
                        value={profileSettings.email}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Role
                      </label>
                      <input
                        type="text"
                        value={profileSettings.role}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed capitalize"
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                      <p className="text-sm text-blue-900 font-medium mb-1">Profile Information</p>
                      <p className="text-sm text-blue-800">
                        Your profile information is managed by your course administrator.
                        If you need to update your details, please contact your instructor or administrator.
                      </p>
                    </div>
                  </div>
                </div>

                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800 font-medium">{errorMessage}</p>
                  </div>
                )}
              </div>
            )}

            {/* Code Editor Settings Tab */}
            {activeTab === 'editor' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Code Editor Settings</h2>

                  {/* Sub-tabs for editor settings */}
                  <div className="border-b border-gray-200 mb-6">
                    <nav className="flex space-x-8">
                      {editorTabs.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => setEditorSubTab(tab.id)}
                          className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                            editorSubTab === tab.id
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </nav>
                  </div>

                  {/* Editor settings content */}
                  <div className="min-h-[400px]">
                    {editorSubTab === 'appearance' && renderAppearanceSettings()}
                    {editorSubTab === 'behavior' && renderBehaviorSettings()}
                    {editorSubTab === 'layout' && renderLayoutSettings()}
                  </div>

                  {/* Save Message */}
                  {editorSaveMessage && (
                    <div className={`p-3 rounded-md text-sm ${
                      editorSaveMessage.includes('successfully')
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-red-100 text-red-700 border border-red-200'
                    }`}>
                      {editorSaveMessage}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                    <div className="text-sm text-gray-500">
                      {hasEditorChanges ? (
                        <span className="text-orange-600 font-medium">⚠️ You have unsaved changes</span>
                      ) : (
                        <span>Settings synced with your account</span>
                      )}
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleResetEditorSettings}
                        disabled={isSavingEditor}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Reset to Defaults
                      </button>
                      <button
                        onClick={handleSaveEditorSettings}
                        disabled={isSavingEditor || !hasEditorChanges}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                          hasEditorChanges
                            ? 'text-white bg-blue-600 border border-transparent hover:bg-blue-700'
                            : 'text-gray-700 bg-gray-100 border border-gray-300'
                        }`}
                      >
                        {isSavingEditor ? 'Saving...' : hasEditorChanges ? 'Save Settings' : 'No Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
